import { useLayoutEffect, useState, RefObject } from 'react';

type ResizeObserverCtor = new (cb: () => void) => {
    observe: (el: Element) => void;
    disconnect: () => void;
};

function getResizeObserver(): ResizeObserverCtor | undefined {
    if (typeof window === 'undefined') {
        return undefined;
    }
    return (window as unknown as { ResizeObserver?: ResizeObserverCtor }).ResizeObserver;
}

/**
 * Pixel distance from the start of a horizontally duplicated row to where the second copy begins
 * (first child index === segmentLength). CSS translate(-50%) does not match that distance when
 * flex gap is used or the two halves differ in width, which causes a visible jump each loop.
 */
export function useMarqueeLoopPx(
    trackRef: RefObject<HTMLElement | null>,
    segmentLength: number,
    layoutKey: string,
): number | null {
    const [loopPx, setLoopPx] = useState<number | null>(null);

    useLayoutEffect(() => {
        const el = trackRef.current;
        if (!el) {
            return;
        }

        const measure = () => {
            const marker = el.children[segmentLength] as HTMLElement | undefined;
            if (!marker) {
                setLoopPx(null);
                return;
            }
            setLoopPx(marker.offsetLeft);
        };

        measure();
        const RO = getResizeObserver();
        if (RO) {
            const ro = new RO(measure);
            ro.observe(el);
            return () => ro.disconnect();
        }
        window.addEventListener('load', measure);
        return () => window.removeEventListener('load', measure);
    }, [layoutKey, segmentLength, trackRef]);

    return loopPx;
}
